import{ScheduledGiftTeasers,ScheduledGiftEditor,ScheduledGiftReviewDetails,ScheduledGiftSetupNotice}from'./ScheduledGiftTeaser';
import { PrivacyPanel, DeletionQueuePanel } from "./PrivacyPanel";
import { DeveloperGuide } from "./DeveloperGuide";
import { AgentPanel } from "./AgentPanel";
import { onDraftPrefill, takeDraftPrefill } from "./draft-prefill";
import{isScheduledGiftAction}from'./scheduled-gift-preparation';
import{ShopPolicyEditor}from'./ShopPolicyEditor';
import{isShopPolicyAction}from'./shop-policy-preparation';
import{CollectionReviewDetails}from'./CollectionReviewDetails';
import{needsCollectionMetadataPreparation}from'./collection-metadata-preparation';
import{CustomPriceReview}from'./CustomPriceReview';
import{customPriceReviewValid}from'./custom-price-review';
import{GuestPurchaseEntry}from'./GuestPurchaseEntry';
import{GuestOrderClaimEntry}from'./GuestOrderClaimEntry';
import{GrowthEntryPanel}from'./GrowthEntryPanel';
import{isWorkspaceBulkAction,workspaceStagingAttachment}from'./workspace-bulk-preparation';
import{needsDigitalDeliveryPreparation}from'./digital-delivery-preparation';
import{HeaderSearch,ProductCategoryNav,ProductHero,ProductHeading,ProductSellerWorkspace,sellerNavLabel,productWords,publicHoldText,productSearchHref}from"./product-experience";
import"./product-experience.css";
import{completeSellerSetup,sellerKindLabel}from"./seller-setup-completion";
import {AccountEntry} from './AccountEntry';
import {countryName} from '../../contracts/src/launch-countries';
import {CheckoutSteps,mergeCheckoutActions,cartPageActions,checkoutWords} from './CheckoutSteps';
import {WhopPayPanel,SellerPayoutsPanel,SellerReviewPanel,SellerReviewQueue,SellerShippingPanel} from './MarketplacePanels';
import {createAccountSessionAdopter,accountEntryFetcher} from './account-session-adoption';
import {SellerDigitalUploadEntry} from './SellerDigitalUploadEntry';
import {sellerDigitalField,needsSellerDigitalPreparation} from './seller-digital-preparation';
import {CatalogUploadEntry} from './CatalogUploadEntry';
import {isCatalogUploadEntry,isCatalogMediaEntry,isCatalogEditorEntry} from './catalog-upload-preparation';
import {isShopSectionsAction} from './shop-sections-preparation';
import {needsSavedItemsPreparation} from './saved-items-preparation';
import {needsPrivateCollectionPreparation} from './private-collections-preparation';
import{needsCommercePreparation}from'./commerce-preparation';
import {ordinaryFulfillment} from './fulfillment-preparation';
import{ordinaryCustom,customDeclarationField}from'./custom-preparation';
import {controlActionLabel,controlFieldLabel} from './control-action-copy';
import {ConversationAttachmentControl} from './ConversationAttachmentControl';
import {PublicationDeclaration,isPublicationDeclaration} from "./PublicationDeclaration";
import {nonSensitiveConversation} from './conversation-preparation';
import {ControlContent,isControlScreen,actionControlId} from "./ControlContent";
import "./control.css";
import {WorkspaceContent,isWorkspaceScreen} from "./WorkspaceContent";
import "./workspace.css";
import {DiscoveryContent,isDiscoveryScreen} from "./DiscoveryContent";
import "./discovery.css";
import { PostPurchaseWorkspace } from "./postpurchase/PostPurchaseWorkspace";
import { postPurchaseScreens } from "./postpurchase/copy";
import { OrderPages, loadOrderContinuation } from "./postpurchase/order-pages";
import "./postpurchase/workspace.css";
import { recoveryText, filterRecoveryLinks } from "./recovery";
import { financialState } from "./financial-state";
import { catalogAttachment, saveAttachment } from "./attachments";
import { CatalogEditor } from "./CatalogEditor";
import { catalogDraft, catalogIssues } from "./catalog-draft";
import { PendingCommands } from "./pending";
import { UploadField } from "./UploadField";
import "../node_modules/@fontsource/lato/400.css";
import "../node_modules/@fontsource/lato/700.css";
import "../node_modules/@fontsource/noto-sans-jp/400.css";
import "../node_modules/@fontsource/noto-sans-jp/700.css";
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  useSyncExternalStore,
  type FormEvent,
} from "react";
import { screenOperations } from "./screen-contracts";
import {
  Transport,
  actionInput,
  fieldValue,
  fieldAvailable,
  validateField,
  ApiError,
  safeHref,
  imageHref,
  requiresReview,
  type Action,
  type View,
  type State,
  type Field,
  type Card,
} from "./transport";
import {
  appScreens,
  matchRoute,
  routeHref,
  localeFor,
  type AppKind,
} from "./router";
import { copy, type Copy } from "./copy";
import {
  formatMoney,
  formatDate,
  type LaunchLocale,
} from "../../locales/src/locales";

type Location = { path: string; query: string };
const current = (): Location => ({
  path: window.location.pathname,
  query: window.location.search,
});
function useLocation() {
  const [location, set] = useState(current);
  useEffect(() => {
    const update = () => set(current());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  const go = useCallback((href: string) => {
    const safe = safeHref(href);
    if (!safe) return;
    history.pushState(null, "", safe);
    set(current());
  }, []);
  return [location, go] as const;
}
function Link({
  href,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const safe = href && safeHref(href);
  return safe ? (
    <a {...rest} href={safe}>
      {children}
    </a>
  ) : (
    <span>{children}</span>
  );
}
function time(instant: string, locale: LaunchLocale) {
  try {
    return formatDate(locale, instant);
  } catch {
    return "—";
  }
}
function Price({
  money,
  locale,
}: {
  money: { currency: string; minor: string };
  locale: LaunchLocale;
}) {
  try {
    return <bdi className="price">{formatMoney(locale, money)}</bdi>;
  } catch {
    return <bdi>—</bdi>;
  }
}
/** Plain-language facts: technical version numbers and bare ids are hidden,
 * timestamps become local dates, and status codes become words. */
export function readableFacts(facts: readonly { label: string; value: string }[] | undefined, locale = "en") {
  return (facts ?? []).flatMap(f => {
    if (/^(version|record version|policy version|versi[oó]n|revision|digest)$/i.test(f.label.trim()) || isOpaqueId(f.value)) return [];
    return [{ label: f.label.replace(/\s*·\s*[0-9a-f]{8}-[0-9a-f-]{27}$/i, "").replace(/\s*·\s*[0-9a-f]{64}$/i, ""), value: readableValue(f.value, locale) }];
  });
}
export function readableValue(v: string, locale = "en") {
  if (typeof v !== "string") return v;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) try { return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }); } catch { return d.toISOString(); }
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) { const d = new Date(v + "T12:00:00Z"); try { return d.toLocaleDateString(locale, { dateStyle: "medium" }); } catch { return v; } }
  if (/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(v)) { const t = v.toLowerCase().replace(/_/g, " "); return t.charAt(0).toUpperCase() + t.slice(1); }
  return v;
}
/** The private preview and local test builds show a "preview" banner; the live site shows legal links. */
const isPreviewHost = () => typeof location === "undefined" || /(^|\.)preview\.|^localhost$|^127\.0\.0\.1$/.test(location.hostname);
const LEGAL_LINKS: readonly (readonly [string, string])[] = [["/legal/terms.html", "Terms of Service"], ["/legal/privacy.html", "Privacy Notice"], ["/legal/seller-agreement.html", "Seller Agreement"], ["/legal/prohibited-items.html", "Prohibited items"], ["/legal/returns-and-buyer-protection.html", "Returns and buyer protection"]];
const productGrid = (screen?: string) => ["B01", "B02", "B04", "B05"].includes(screen ?? "");
const genericTitle = (locale: string) => ({ en: "Details", es: "Detalles", fr: "Détails", de: "Details", ja: "詳細" } as Record<string, string>)[locale] ?? "Details";
const fallbackTitleFor = (screen: string | undefined, locale: string) => ["B07", "B08"].includes(screen ?? "")
  ? ({ en: "Your cart", es: "Tu carrito", fr: "Votre panier", de: "Dein Warenkorb", ja: "カート" } as Record<string, string>)[locale] ?? "Your cart"
  : screen === "B10" ? ({ en: "Order", es: "Pedido", fr: "Commande", de: "Bestellung", ja: "注文" } as Record<string, string>)[locale] ?? "Order" : undefined;
function Facts({ facts: given, locale = "en", raw = false }: { facts?: { label: string; value: string }[]; locale?: string; raw?: boolean }) {
  const facts = raw ? given : readableFacts(given, locale);
  return facts?.length ? (
    <dl className="facts">
      {facts.map((f, i) => (
        <React.Fragment key={i}>
          <dt>{f.label}</dt>
          <dd>
            <bdi>{f.value}</bdi>
          </dd>
        </React.Fragment>
      ))}
    </dl>
  ) : null;
}
function Product({
  card,
  locale,
  c,
  listing = false,
  showMedia = true,
  apiOrigin,
  grid = false,
  fallbackTitle,
}: {
  card: Card;
  locale: LaunchLocale;
  c: Copy;
  listing?: boolean;
  showMedia?: boolean;
  apiOrigin: string;
  grid?: boolean;
  fallbackTitle?: string;
}) {
  const [original, setOriginal] = useState(false);
  const image = card.image && imageHref(card.image.url, apiOrigin);
  return (
    <article className={"product " + (listing ? "listing" : "")}>
      {showMedia && image && (
        <Link href={card.href}><img
          className="product-image"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          src={image}
          alt={card.image!.alt}
          loading={listing ? "eager" : "lazy"}
          onError={(e) => {
            e.currentTarget.hidden = true;
          }}
        /></Link>
      )}
      {showMedia&&!image&&typeof card.href==="string"&&/^\/(?:en|de|fr|es|ja|ar|he)\/listing\//.test(card.href)&&<div className="product-image-placeholder" aria-hidden="true"><img src="/brand/icon.svg" alt="" width="96" height="93"/></div>}
      <div className="product-copy">
        <div className="badges">
          {card.sponsored && <span>{c.sponsored}</span>}
          {card.demo && <strong>{c.demo}</strong>}
          {card.kind && (
            <span>
              {Object.hasOwn(c.kinds, card.kind)
                ? c.kinds[card.kind as keyof typeof c.kinds]
                : card.kind}
            </span>
          )}
        </div>
        <h2 dir="auto">
          <Link href={card.href}>{isOpaqueId(card.title) ? (card.id.startsWith("captured-shop-policy-") ? ({ en: "Shop policy", es: "Política de la tienda", fr: "Politique de la boutique", de: "Shop-Richtlinie", ja: "ショップのポリシー" } as Record<string, string>)[locale] ?? "Shop policy" : fallbackTitle ?? genericTitle(locale)) : card.title}</Link>
        </h2>
        <p dir="auto">{card.description}</p>
        {card.translation && (
          <div>
            <p dir="auto">{original ? card.original : card.translation}</p>
            <small>
              {original ? c.original : c.translated} · {card.attribution}
            </small>
            <button
              type="button"
              className="text-button"
              onClick={() => setOriginal(!original)}
            >
              {original ? c.translated : c.original}
            </button>
          </div>
        )}
        {card.money && !card.demo && (
          <Price money={card.money} locale={locale} />
        )}
        {/* Grid cards stay simple: only the shop name. Full details are on the product page. */}
        <Facts locale={locale} facts={grid ? card.facts?.filter((f) => /^(shop|tienda|boutique|ショップ)$/i.test(f.label)) : card.facts} />
      </div>
    </article>
  );
}
function Input({
  field,
  value,
  onChange,
  disabled,
  c,
  prefix,
  optionLabel,
}: {
  field: Field;
  optionLabel?: (value:string)=>string;
  value: unknown;
  onChange: (v: string | boolean) => void;
  disabled: boolean;
  c: Copy;
  prefix: string;
}) {
  const id = prefix + "-" + field.name;
  const common = {
    id,
    name: field.name,
    required: field.type === "checkbox" ? field.mustAccept : field.required,
    disabled,
    "aria-describedby": id + "-hint",
  };
  return (
    <div className="field">
      <label htmlFor={id}>
        {field.label}
        {field.required ? " *" : ""}
      </label>
      {field.type === "textarea" ? (
        <textarea
          {...common}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          maxLength={20000}
        />
      ) : field.type === "select" ? (
        <select
          {...common}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{c.choose}</option>
          {(/country$/i.test(field.name)&&optionLabel?[...(field.options??[])].sort((a,b)=>optionLabel(a).localeCompare(optionLabel(b))):field.options)?.map((o) => <option key={o} value={o}>{optionLabel?optionLabel(o):/^[a-z0-9]+(-[a-z0-9]+)*$/.test(o)?o.charAt(0).toUpperCase()+o.slice(1).replace(/-/g," "):/^[a-z]+([A-Z][a-z0-9]*)+$/.test(o)?o.charAt(0).toUpperCase()+o.slice(1).replace(/([A-Z])/g," $1").toLowerCase().replace(/^./,m=>m.toUpperCase()):o}</option>)}
        </select>
      ) : field.type === "checkbox" ? (
        <input
          {...common}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      ) : (
        <input
          {...common}
          type={field.type}
          value={String(value ?? "")}
          min={field.min}
          max={field.max}
          step={field.type === "number" ? 1 : undefined}
          maxLength={20000}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <small id={id + "-hint"}>{field.required ? c.required : ""}</small>
    </div>
  );
}
function ReviewDialog({
  action,
  view,
  locale,
  c,
  onCancel,
  onConfirm,
  returnFocus,
}: {
  action: Action;
  view: View;
  locale: LaunchLocale;
  c: Copy;
  onCancel: () => void;
  onConfirm: () => void;
  returnFocus?: HTMLElement;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [now, setNow] = useState(Date.now());
  const [checked, setChecked] = useState(false);
  const expired =
    !action.expiresAt ||
    !Number.isFinite(Date.parse(action.expiresAt)) ||
    Date.parse(action.expiresAt) <= now;
  useEffect(() => {
    const previous = returnFocus || (document.activeElement as HTMLElement);
    ref.current?.showModal();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(t);
      if (previous?.isConnected && !previous.matches(":disabled"))
        previous.focus();
      else document.getElementById("main")?.focus();
    };
  }, []);
  const confirm = action.confirmation;
  return (
    <dialog
      ref={ref}
      aria-labelledby="review-title"
      onKeyDown={(e) => {
        if (e.key !== "Tab") return;
        const controls = Array.from(
          e.currentTarget.querySelectorAll<HTMLElement>(
            'button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),a[href],[tabindex="0"]',
          ),
        ).filter((node) => node.getClientRects().length > 0);
        const first = controls[0],
          last = controls[controls.length - 1];
        if (!first) {
          e.preventDefault();
          e.currentTarget.focus();
          return;
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <h2 id="review-title">{c.pack["confirmation.review"]}</h2>
      <CollectionReviewDetails action={action} locale={locale} />
      {isScheduledGiftAction(action)&&<ScheduledGiftReviewDetails action={action} locale={locale}/>}
      <p>{c.review}</p>
      {!isOpaqueId(action.subject) && <h3>{action.subject}</h3>}
      {confirm?.preparedByAgent && <p className="notice">{c.agent}</p>}
      <details className="technical-details">
      <summary>{({en:"Technical details",es:"Detalles técnicos",fr:"Détails techniques",de:"Technische Details",ja:"技術的な詳細"} as Record<string,string>)[locale]??"Technical details"}</summary>
      <Facts
        raw
        facts={[
          {
            label: c.pack["confirmation.resource"],
            value: confirm?.resource.id || "—",
          },
          {
            label: c.version,
            value: String(confirm?.resource.version ?? "—"),
          },
          { label: c.session, value: confirm?.principal || "—" },
          { label: "SHA-256", value: confirm?.digest || "—" },
          {
            label: c.expires,
            value: action.expiresAt ? time(action.expiresAt, locale) : "—",
          },
        ]}
      />
      </details>
      {confirm?.amount && <Price money={confirm.amount} locale={locale} />}
      <CustomPriceReview action={action} locale={locale} />
      {confirm?.before && (
        <p>
          <del>{confirm.before}</del>
        </p>
      )}
      {confirm?.after && (
        <p>
          <ins>{confirm.after}</ins>
        </p>
      )}
      {expired && <p role="status">{c.expiry}</p>}
      <label className="check">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        {c.pack["operator.reviewer.confirm"]}{isOpaqueId(action.subject) ? "" : ": " + action.subject}
      </label>
      <div className="actions">
        <button onClick={onCancel}>{c.pack["confirmation.cancel"]}</button>
        <button
          className="primary"
          disabled={!checked || expired || !customPriceReviewValid(action)}
          onClick={onConfirm}
        >
          {c.pack["confirmation.confirm"]}
        </button>
      </div>
    </dialog>
  );
}
function ResourceActions({
  view,
  api,
  pending,
  locale,
  c,
  onState,
  onRefresh,
  blocked,
  initialDrafts,
  onDraftsChange,
  onSellerSetupCompleted,
}: {
  view: View;
  onSellerSetupCompleted:(epoch:number,input:unknown,outcome:unknown)=>boolean;
  api: Transport;
  pending: PendingCommands;
  locale: LaunchLocale;
  c: Copy;
  onState: (s: State, e?: ApiError) => void;
  onRefresh: () => void;
  blocked: boolean;
  initialDrafts: Record<string, Record<string, unknown>>;
  onDraftsChange: (drafts: Record<string, Record<string, unknown>>) => void;
}) {
  useSyncExternalStore(pending.subscribe, pending.snapshot);
  const [drafts, setDrafts] =
    useState<Record<string, Record<string, unknown>>>(initialDrafts);
  const [review, setReview] = useState<Action | null>(null);
  const activeReview=useRef<Action|null>(null);
  const workBusy=useRef(false);
  const reviewOrigin = useRef<string>("");
  const reviewTrigger = useRef<HTMLElement | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(new Set<string>());
  const [invalid, setInvalid] = useState<string[]>([]);
  const summary = useRef<HTMLDivElement>(null);
  const attachmentCleanup = useRef(new Set<() => void>());
  const mounted = useRef(true);
  const executionOwner=useRef({api,pending,locale,id:view.id,version:view.version,view});
  executionOwner.current={api,pending,locale,id:view.id,version:view.version,view};
  const policyPreparations=useRef(new WeakMap<Action,()=>boolean>());
  const scheduledPreparations=useRef(new WeakMap<Action,()=>boolean>());
  const scheduledEpoch=api.sessionEpoch;
  const scheduledReview=useRef<Action|null>(null);scheduledReview.current=review&&isScheduledGiftAction(review)?review:null;
  useEffect(()=>()=>{const old=scheduledReview.current;if(old)api.releaseScheduledGiftMedia(old)},[api,pending,locale,view]);
  useEffect(()=>{if(activeReview.current&&isScheduledGiftAction(activeReview.current))activeReview.current=null;setReview(prior=>prior&&isScheduledGiftAction(prior)?null:prior)},[api,pending,locale,view,scheduledEpoch]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const cleanup of attachmentCleanup.current) cleanup();
      attachmentCleanup.current.clear();
    };
  }, []);
  useEffect(() => {
    if (!review?.expiresAt) return;
    const timer = setInterval(() => {
      if (Date.parse(review.expiresAt!) <= Date.now()) {
        if(isScheduledGiftAction(review))api.releaseScheduledGiftMedia(review);
        if(activeReview.current===review)activeReview.current=null;
        setReview(null);
        onState("stale", new ApiError("stale", "CONFIRMATION_EXPIRED"));
      }
    }, 500);
    return () => clearInterval(timer);
  }, [review]);
  // Fill the create-draft form from an AI agent suggestion the seller chose.
  useEffect(() => {
    const apply = () => {
      const create = view.actions.find((a) => a.id === "catalog.draft.create");
      if (!create) return;
      const values = takeDraftPrefill();
      if (values) setDrafts((prev) => ({ ...prev, [create.instanceId]: { ...(prev[create.instanceId] || {}), ...values } }));
    };
    apply();
    return onDraftPrefill(apply);
  }, [view]);
  const values = (a: Action) => actionInput(a, drafts[a.instanceId] || {});
  const customFields=(a:Action)=>(a.fields||[]).map(f=>customDeclarationField(a,f,drafts[a.instanceId]||{}));
  const invalidFields = (a: Action) =>
    customFields(a)
      .filter((f) => fieldAvailable(a, f, drafts[a.instanceId] || {}))
      .filter((f) => {
        const v = fieldValue(a, f, drafts[a.instanceId] || {});
        return !validateField(f, v);
      });
  async function execute(action: Action) {
    if (workBusy.current || busy || blocked || completed.has(action.instanceId)) return;
    if(isScheduledGiftAction(action)&&!scheduledPreparations.current.get(action)?.()){
      api.releaseScheduledGiftMedia(action);onState("stale",new ApiError("stale","SOURCE_VIEW_CHANGED"));return;
    }
    if (
      requiresReview(action) &&
      (!action.expiresAt ||
        !Number.isFinite(Date.parse(action.expiresAt)) ||
        Date.parse(action.expiresAt) <= Date.now())
    ) {
      onState("stale", new ApiError("stale", "CONFIRMATION_EXPIRED"));
      return;
    }
    activeReview.current=null;
    workBusy.current=true;
    setReview(null);
    setBusy(true);
    const dispatchEpoch=api.sessionEpoch;
    const dispatchPath=window.location.pathname,dispatchQuery=window.location.search;
    const current=()=>mounted.current&&executionOwner.current.api===api&&executionOwner.current.pending===pending&&executionOwner.current.locale===locale&&executionOwner.current.id===view.id&&executionOwner.current.version===view.version&&api.sessionEpoch===dispatchEpoch&&window.location.pathname===dispatchPath&&window.location.search===dispatchQuery;
    try {
      const input = action === review ? action.input : values(action);
      const selected = (((nonSensitiveConversation.has(action.id) || ordinaryFulfillment.has(action.id) || ordinaryCustom.has(action.id) || isCatalogMediaEntry(action)) && !requiresReview(action) && action.fields?.length) || isCatalogEditorEntry(action) || needsCommercePreparation(action) || needsCollectionMetadataPreparation(action) || needsPrivateCollectionPreparation(action) || needsSavedItemsPreparation(action) || isShopSectionsAction(action) || needsSellerDigitalPreparation(action) || needsDigitalDeliveryPreparation(action)) ? await api.prepare(action,input) : action;
      if(!current())return;
      const outcome = await pending.submit(selected, selected===action?input:selected.input, selected.resource!.id);
      if (current()) {
        if(action.id === "identity.sellerSetup")onSellerSetupCompleted(dispatchEpoch,selected.input,outcome);
        if(!current())return;
        if (action.id === "workspace.export" && outcome.kind === "ok") {
          const attachment=isWorkspaceBulkAction(action)?await workspaceStagingAttachment(action,outcome.value):catalogAttachment(outcome.value, input.format);
          // Hash verification can yield. Recheck the actual owner and browser
          // route after that await, before any Blob URL, click or state update.
          if(!current())return;
          attachmentCleanup.current.add(saveAttachment(attachment));
        }
        setCompleted(
          (prior) =>
            new Set([
              ...prior,
              action.instanceId,
              ...(action === review && reviewOrigin.current
                ? [reviewOrigin.current]
                : []),
            ]),
        );
        onState("success");
        if (action.id !== "workspace.export") onRefresh();
      }
    } catch (e) {
      if (current()) {
        const err =
          e instanceof ApiError ? e : new ApiError("error", "INVALID_RESPONSE");
        onState(err.state, err);
      }
    } finally {
      if (current()){workBusy.current=false;setBusy(false)}
    }
  }
  function submit(action: Action, e: FormEvent) {
    e.preventDefault();
    reviewTrigger.current = document.activeElement as HTMLElement;
    const bad = invalidFields(action);
    const content =
      action.id === "catalog.draft" &&
      catalogDraft(
        drafts[action.instanceId]?.catalogContent || action.input.content,
      );
    if (content && catalogIssues(content).length) {
      setInvalid(
        catalogIssues(content).map((name) => action.instanceId + "-" + name),
      );
      requestAnimationFrame(() => summary.current?.focus());
      return;
    }
    setInvalid(bad.map((f) => action.instanceId + "-" + f.name));
    if (bad.length) {
      requestAnimationFrame(() => summary.current?.focus());
      return;
    }
    if (requiresReview(action) || action.id === "delegation.prepare") {
      if (
        busy ||
        blocked ||
        completed.has(action.instanceId) ||
        (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())
      )
        return;
      workBusy.current=true;
      setBusy(true);
      const epoch = api.sessionEpoch;
      const reviewPath=window.location.pathname,reviewQuery=window.location.search;
      const currentReview=()=>mounted.current&&executionOwner.current.api===api&&executionOwner.current.pending===pending&&executionOwner.current.locale===locale&&executionOwner.current.id===view.id&&executionOwner.current.version===view.version&&api.sessionEpoch===epoch&&window.location.pathname===reviewPath&&window.location.search===reviewQuery;
      void Promise.resolve()
        .then(() => {
          if (!currentReview())
            throw new ApiError("stale", "SOURCE_VIEW_CHANGED");
          return api.prepare(action, values(action));
        })
        .then((prepared) => {
          if (currentReview()) {
            if(activeReview.current)return;
            activeReview.current=prepared;
            reviewOrigin.current = action.instanceId;
            if(isScheduledGiftAction(action))scheduledPreparations.current.set(prepared,()=>currentReview()&&executionOwner.current.view===view);
            setReview(prepared);
          }
        })
        .catch((error) => {
          if (currentReview()) {
            const err =
              error instanceof ApiError
                ? error
                : new ApiError("error", "INVALID_RESPONSE");
            onState(err.state, err);
          }
        })
        .finally(() => {
          if (mounted.current){workBusy.current=false;setBusy(false)}
        });
    } else void execute(action);
  }
  const policyActions=view.actions.filter(isShopPolicyAction);
  const scheduledActions=view.actions.filter(a=>a.id==='gifting.schedule'||a.id==='gifting.schedule.media');
  const ordered = view.actions.filter(a=>!isShopPolicyAction(a)&&a.id!=='gifting.schedule'&&a.id!=='gifting.schedule.media');
  return (
    <section aria-label={c.pack["confirmation.subject"]}>
      {/* Current permissions remain checked by the unchanged server, without repeated implementation copy. */}
      {invalid.length > 0 && (
        <div ref={summary} tabIndex={-1} role="alert" className="notice error">
          <h2>{c.pack["validation.error.summary.title"]}</h2>
          {invalid.map((id) => (
            <p key={id}>
              <a
                href={"#" + id}
                onClick={(event) => {
                  const target = document.getElementById(id);
                  const control = target?.matches(
                    "input,textarea,select,button",
                  )
                    ? target
                    : target?.querySelector<HTMLElement>(
                        "input,textarea,select,button",
                      );
                  if (control) {
                    event.preventDefault();
                    (control as HTMLElement).focus();
                  }
                }}
              >
                {view.actions
                  .flatMap((a) =>
                    (a.fields || [])
                      .filter((f) => a.instanceId + "-" + f.name === id)
                      .map((f) => controlFieldLabel(a,f.label,locale)),
                  )
                  .join(", ") || id.split("-").slice(-2).join(" ")}
                : {c.pack["validation.invalid"]}
              </a>
            </p>
          ))}
        </div>
      )}
      {Object.entries(drafts)
        .filter(([instance]) => !ordered.some((a) => a.instanceId === instance))
        .map(([instance, draft]) => {
          const safe = Object.entries(draft).filter(
            ([name, value]) =>
              !/password|token|proof|secret|credential|key|confirmation|approval|challenge|receipt/i.test(
                name,
              ) && ["string", "boolean", "number"].includes(typeof value),
          );
          return safe.length ? (
            <details className="notice" key={instance}>
              <summary>{c.draft}</summary>
              <p>{c.retryUnsafe}</p>
              {safe.map(([name, value]) => (
                <p key={name}>
                  <strong>{name}</strong>: <output>{String(value)}</output>
                </p>
              ))}
            </details>
          ) : null;
        })}
      {policyActions.map(action=><ShopPolicyEditor key={action.instanceId+":"+window.location.pathname+":"+window.location.search} action={action} locale={locale} sessionEpoch={api.sessionEpoch}
        disabled={busy||!!review||blocked||(pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())||!!action.disabledReason||!!action.expiresAt&&Date.parse(action.expiresAt)<=Date.now()}
        prepare={async(a,input)=>{
          const epoch=api.sessionEpoch,path=window.location.pathname,query=window.location.search;
          const current=()=>mounted.current&&executionOwner.current.api===api&&executionOwner.current.pending===pending&&executionOwner.current.locale===locale&&executionOwner.current.view===view&&view.actions.includes(a)&&api.sessionEpoch===epoch&&window.location.pathname===path&&window.location.search===query;
          if(!current())throw new ApiError("stale","SOURCE_VIEW_CHANGED");
          const prepared=await api.prepare(a,input);
          if(!current())throw new ApiError("stale","SOURCE_VIEW_CHANGED");
          policyPreparations.current.set(prepared,current);
          return prepared;
        }}
        save={async prepared=>{
          const current=policyPreparations.current.get(prepared);
          if(!current?.())throw new ApiError("stale","SOURCE_VIEW_CHANGED");
          try{const result=await pending.submit(prepared,prepared.input,prepared.resource!.id);
            if(current()){onState("success");onRefresh()}return result;
          }catch(e){if(current()){const error=e instanceof ApiError?e:new ApiError("error","INVALID_RESPONSE");onState(error.state,error)}throw e;}
        }}/>)}
      {!!scheduledActions.length&&<ScheduledGiftEditor actions={scheduledActions} api={api} pending={pending} locale={locale}
        routeKey={view.id+':'+view.version+':'+window.location.pathname+window.location.search} disabled={busy||!!review||blocked}
        onInvalidate={origin=>{if(review&&isScheduledGiftAction(review)&&reviewOrigin.current===origin){api.releaseScheduledGiftMedia(review);if(activeReview.current===review)activeReview.current=null;setReview(null)}}}
        onFailure={error=>onState(error.state,error)}
        onPrepared={(prepared,origin,trigger)=>{
          const path=window.location.pathname,query=window.location.search;
          const current=()=>mounted.current&&executionOwner.current.api===api&&executionOwner.current.pending===pending&&executionOwner.current.locale===locale&&executionOwner.current.view===view&&api.sessionEpoch===scheduledEpoch&&window.location.pathname===path&&window.location.search===query&&view.actions.some(a=>a.instanceId===origin&&(a.id==='gifting.schedule'||a.id==='gifting.schedule.media'));
          if(!current()||activeReview.current||workBusy.current){api.releaseScheduledGiftMedia(prepared);return}
          activeReview.current=prepared;
          reviewTrigger.current=trigger??document.activeElement as HTMLElement;reviewOrigin.current=origin;scheduledPreparations.current.set(prepared,current);setReview(prepared);
        }}/>}
      {!ordered.length&&!policyActions.length&&!scheduledActions.length && <p>{productWords(locale).noAction}</p>}
      {ordered.map((action) => (
        <form
          id={actionControlId(action.instanceId)}
          tabIndex={-1}
          noValidate
          className="action-panel"
          key={action.instanceId}
          onSubmit={(e) => submit(action, e)}
        >
          <h2>{controlActionLabel(action,locale)}</h2>
          {!isOpaqueId(action.subject) && <p>{action.subject}</p>}
          {isCatalogEditorEntry(action) &&
            catalogDraft(
              drafts[action.instanceId]?.catalogContent || action.input.content,
            ) && (
              <CatalogEditor
                prefix={action.instanceId}
                locale={locale}
                disabled={
                  busy ||
                  !!review ||
                  blocked ||
                  completed.has(action.instanceId) ||
                  (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())
                }
                value={
                  catalogDraft(
                    drafts[action.instanceId]?.catalogContent ||
                      action.input.content,
                  )!
                }
                onChange={(content) =>
                  setDrafts((prev) => {
                    const next = {
                      ...prev,
                      [action.instanceId]: {
                        ...prev[action.instanceId],
                        catalogContent: content,
                      },
                    };
                    onDraftsChange(next);
                    return next;
                  })
                }
              />
            )}
          {customFields(action)
            .filter((field) =>
              fieldAvailable(action, field, drafts[action.instanceId] || {}),
            )
            .map((field) =>
              field.type === "file" ? (
                <UploadField
                  key={field.name}
                  field={{...field,label:controlFieldLabel(action,field.label,locale)}}
                  api={api}
                  pending={pending}
                  resource={action.resource!}
                  c={c}
                  disabled={
                    busy ||
                    !!review ||
                    blocked ||
                    completed.has(action.instanceId) ||
                    (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())
                  }
                  value={fieldValue(
                    action,
                    field,
                    drafts[action.instanceId] || {},
                  )}
                  prefix={action.instanceId}
                  onChange={(v) =>
                    setDrafts((prev) => {
                      const next = {
                        ...prev,
                        [action.instanceId]: {
                          ...prev[action.instanceId],
                          [field.name]: v,
                        },
                      };
                      onDraftsChange(next);
                      return next;
                    })
                  }
                />
              ) : (
                <Input
                  optionLabel={action.id === "identity.sellerSetup" && field.name === "kind" ? value=>sellerKindLabel(value,locale) : /country$/i.test(field.name) ? value=>countryName(value,locale) : undefined}
                  key={field.name}
                  field={{...field,label:controlFieldLabel(action,field.label,locale)}}
                  value={fieldValue(
                    action,
                    field,
                    drafts[action.instanceId] || {},
                  )}
                  c={c}
                  disabled={
                    busy ||
                    !!review ||
                    blocked ||
                    completed.has(action.instanceId) ||
                    (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())
                  }
                  prefix={action.instanceId}
                  onChange={(v) =>
                    setDrafts((prev) => {
                      const next = {
                        ...prev,
                        [action.instanceId]: {
                          ...prev[action.instanceId],
                          [field.name]: v,
                        },
                      };
                      onDraftsChange(next);
                      return next;
                    })
                  }
                />
              ),
            )}
          {action.disabledReason && <p>{/^[A-Z][A-Z0-9_]{0,95}$/.test(action.disabledReason)?publicHoldText(action.disabledReason,"denied",locale):action.disabledReason}</p>}
          <button
            className="primary"
            disabled={
              busy ||
              !!review ||
              blocked ||
              completed.has(action.instanceId) ||
              (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked()) ||
              (action.fields || []).some(
                (f) =>
                  f.type === "file" &&
                  (
                    fieldValue(
                      action,
                      f,
                      drafts[action.instanceId] || {},
                    ) as any
                  )?.uploadPending,
              ) ||
              !!action.disabledReason ||
              (!!action.expiresAt && Date.parse(action.expiresAt) <= Date.now())
            }
          >
            {requiresReview(action)
              ? c.pack["confirmation.review"]
              : controlActionLabel(action,locale)}
          </button>
        </form>
      ))}
      {review && (
        <ReviewDialog
          key={review.instanceId}
          action={review}
          returnFocus={reviewTrigger.current}
          view={view}
          locale={locale}
          c={c}
          onCancel={() => {activeReview.current=null;if(isScheduledGiftAction(review))api.releaseScheduledGiftMedia(review);setReview(null)}}
          onConfirm={() => void execute(review)}
        />
      )}
    </section>
  );
}
function Search({
  query,
  locale,
  c,
  onGo,
}: {
  query: string;
  locale: LaunchLocale;
  c: Copy;
  onGo: (s: string) => void;
}) {
  const params = new URLSearchParams(query);
  return (
    <form
      className="search"
      action={"/" + locale + "/search"}
      onSubmit={(e) => {
        e.preventDefault();
        const fields=new FormData(e.currentTarget),changes=Object.fromEntries(["q","destination","kind","sort"].map(key=>[key,String(fields.get(key)??"")]));
        onGo(productSearchHref(locale,query,changes));
      }}
    >
      <div className="field search-query">
        <label htmlFor="q">{c.query}</label>
        <input
          id="q"
          name="q"
          type="search"
          defaultValue={params.get("q") || ""}
          maxLength={300}
        />
      </div>
      <div className="field">
        <label htmlFor="destination">{c.destination}</label>
        <input
          name="destination"
          id="destination"
          defaultValue={params.get("destination") || ""}
          maxLength={2}
          pattern="[A-Za-z]{2}"
          autoComplete="country"
        />
      </div>
      <div className="field">
        <label htmlFor="kind">{c.kind}</label>
        <select id="kind" name="kind" defaultValue={params.get("kind") || ""}>
          <option value="">{c.all}</option>
          {[
            "handmade",
            "designed",
            "vintage",
            "supply",
            "digital",
            "custom",
          ].map((s) => (
            <option key={s} value={s}>
              {c.kinds[s as keyof typeof c.kinds]}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="sort">{c.sort}</label>
        <select
          id="sort"
          name="sort"
          defaultValue={params.get("sort") || "relevance"}
        >
          <option value="relevance">{c.relevance}</option>
          <option value="newest">{c.newest}</option>
          <option value="price">{c.price}</option>
        </select>
      </div>
      <button className="primary">{c.pack["nav.search"]}</button>
    </form>
  );
}
/** Internal record ids (UUIDs) are not useful to people; hide them in labels. */
const isOpaqueId = (v: unknown) => typeof v === "string" && (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) || /^[0-9a-f]{64}$/i.test(v));
function Session({
  legacy,
  signinHref,
  api,
  c,
  locale,
  onChange,
  onError,
}: {
  legacy:boolean;
  signinHref:string;
  api: Transport;
  c: Copy;
  locale: LaunchLocale;
  onChange: () => void;
  onError: (e: ApiError) => void;
}) {
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => {
    let live=true;const epoch=api.sessionEpoch;
    setLabel("");setExpiresAt("");setPassword("");
    if(api.authenticated)void api.session().then(current=>{
      if(live&&epoch===api.sessionEpoch){setLabel(current.label);setExpiresAt(current.expiresAt)}
    }).catch(error=>{if(live&&epoch===api.sessionEpoch)onError(error instanceof ApiError?error:new ApiError("error","INVALID_SESSION"))});
    return()=>{live=false};
  }, [api, api.sessionEpoch]);
  useEffect(() => {
    if (!expiresAt) return;
    const epoch=api.sessionEpoch;
    const expire = () => {
      if (epoch===api.sessionEpoch && Date.parse(expiresAt) <= Date.now()) {
        api.setSession("");
        setLabel("");
        setExpiresAt("");
        setPassword("");
        onChange();
      }
    };
    const timer = setInterval(expire, 1000);
    window.addEventListener("focus", expire);
    document.addEventListener("visibilitychange", expire);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", expire);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [api, expiresAt, onChange]);
  const [busy, setBusy] = useState(false);
  if(!api.authenticated&&!legacy)return <Link className="account-signin-link" href={signinHref}>{productWords(locale).signin}</Link>;
  return (
    <details className="session">
      <summary>{label && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(label) ? productWords(locale).account + ": " + label : api.authenticated ? productWords(locale).account : c.signin}</summary>
      {legacy&&<p>{c.localNotice}</p>}
      {label && expiresAt && (
        <p>
          {c.expires}:{" "}
          <time dateTime={expiresAt}>{time(expiresAt, locale)}</time>
        </p>
      )}
      {api.authenticated ? (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const revocation = api.signOut();
              setLabel("");
              setPassword("");
              onChange();
              await revocation;
            } catch (e) {
              onError(e as ApiError);
            } finally {
              setBusy(false);
            }
          }}
        >
          {c.signout}
        </button>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              onChange();
              const r = await api.signIn(username, password);
              setLabel(r.label);
              setExpiresAt(r.expiresAt);
              onChange();
            } catch (e) {
              onError(e as ApiError);
            } finally {
              setPassword("");
              setBusy(false);
            }
          }}
        >
          <label htmlFor="username">{c.username}</label>
          <input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="off"
          />
          <label htmlFor="password">{c.password}</label>
          <input
            id="password"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            maxLength={1024}
          />
          <button disabled={busy}>{c.signin}</button>
        </form>
      )}
    </details>
  );
}
function PendingRecovery({
  pending,
  api,
  c,
  onState,
  onRefresh,
}: {
  pending: PendingCommands;
  api: Transport;
  c: Copy;
  onState: (s: State, e?: ApiError) => void;
  onRefresh: () => void;
}) {
  useSyncExternalStore(pending.subscribe, pending.snapshot);
  const [, setRevision] = useState(0);
  const keys = pending.references;
  const setKeys = (_keys: string[]) => setRevision((n) => n + 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!api.authenticated) return;
    let alive = true;
    pending
      .recover()
      .then(() => {
        if (alive) setKeys(pending.references);
      })
      .catch(() => {
        if (alive) setKeys(pending.references);
      });
    return () => {
      alive = false;
    };
  }, [pending, api, api.sessionEpoch]);
  if (!keys.length) return null;
  return (
    <section className="notice" aria-label={c.pending}>
      <h2>{c.pending}</h2>
      <p>{c.retryUnsafe}</p>
      {keys.map((key) => (
        <div key={key}>
          <bdi>{key}</bdi>
          <button
            disabled={busy || !api.authenticated}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await pending.inspect(key);
                if (result.state !== "committed")
                  onState(
                    "pending",
                    new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key),
                  );
                else if (result.outcome?.kind === "ok") {
                  onState("success");
                  onRefresh();
                } else if (
                  result.outcome?.kind === "denied" ||
                  result.outcome?.kind === "conflict"
                )
                  onState(
                    result.outcome.kind === "conflict" ? "stale" : "denied",
                    new ApiError(
                      result.outcome.kind === "conflict" ? "stale" : "denied",
                      result.outcome.code,
                      result.outcome.referenceId,
                    ),
                  );
                setKeys(pending.references);
              } catch (e) {
                onState((e as ApiError).state, e as ApiError);
              } finally {
                setBusy(false);
              }
            }}
          >
            {c.status}
          </button>
        </div>
      ))}
    </section>
  );
}
export function Marketplace({
  kind,
  origin,
}: {
  kind: AppKind;
  origin: string;
}) {
  const [location, go] = useLocation();
  const locale = localeFor(location.path);
  const [workspaceLocale, setWorkspaceLocale] = useState<LaunchLocale>(locale);
  const effective = kind === "storefront" ? locale : workspaceLocale;
  const c = copy(effective);
  const rtl =
    ["ar", "he"].includes(location.path.split("/")[1]) ||
    new URLSearchParams(location.query).get("dir") === "rtl";
  const api = useMemo(() => new Transport(origin), [origin]);
  const pending = useMemo(() => {
    let storage: Storage | undefined;
    try {
      storage = window.sessionStorage;
    } catch {}
    return new PendingCommands(api, storage);
  }, [api]);
  const [identityEpoch, setIdentityEpoch] = useState(0);
  const accountOwnerRef=useRef({api,effective});accountOwnerRef.current={api,effective};
  const accountAdopter=useMemo(()=>createAccountSessionAdopter(()=>({api:accountOwnerRef.current.api,path:window.location.pathname+window.location.search,locale:localeFor(window.location.pathname)})),[api]);
  useLayoutEffect(()=>{accountAdopter.activate();return()=>accountAdopter.dispose()},[accountAdopter]);
  const accountOwnerKey=accountAdopter.capture();
  const accountFetch=useMemo(()=>accountEntryFetcher(api),[api]);
  const screen = matchRoute(kind, location.path);
  const [view, setView] = useState<View>();
  const [state, setState] = useState<State>("loading");
  const [error, setError] = useState<ApiError>();
  const [reload, setReload] = useState(0);
  const [historyStatus, setHistoryStatus] = useState<string[]>([]);
  const sequence = useRef(0);
  const orderPages = useRef(new OrderPages());
  const [pageBusy, setPageBusy] = useState(false);
  const downloadCleanup = useRef(new Set<() => void>());
  useEffect(
    () => () => {
      // Teardown also invalidates an already-running private read/download.
      sequence.current++;
      orderPages.current.reset();
      for (const cleanup of downloadCleanup.current) cleanup();
      downloadCleanup.current.clear();
    },
    [],
  );
  const draftCache = useRef(
    new Map<string, Record<string, Record<string, unknown>>>(),
  );
  useEffect(
    () =>
      api.onSessionRevoked(() => {
        // Only the transport's current request can publish revocation. Advancing
        // the read sequence also fences already queued private projections.
        sequence.current++;
        orderPages.current.reset();
        for (const cleanup of downloadCleanup.current) cleanup();
        downloadCleanup.current.clear();
        draftCache.current.clear();
        setView(undefined);
        setHistoryStatus([]);
        setIdentityEpoch(api.sessionEpoch);
        pending.resetIdentity();
        setError(new ApiError("denied", "AUTH_REQUIRED"));
        setState("denied");
      }),
    [api, pending],
  );
  const draftKey = location.path + ":" + effective;
  const refresh = () => setReload((v) => v + 1);
  const stateChange = (s: State, e?: ApiError) => {
    setState(s);
    setError(e);
    if (s === "success") setHistoryStatus((v) => [...v, c.success]);
    if (s === "denied") {
      draftCache.current.clear();
      setView(undefined);
    }
  };
  useEffect(() => setHistoryStatus([]), [location.path, effective]);
  const firstNavigation = useRef(true);
  useEffect(() => {
    if (firstNavigation.current) {
      firstNavigation.current = false;
      return;
    }
    document.getElementById("main")?.focus();
  }, [location.path]);
  useEffect(() => {
    document.documentElement.lang = effective;
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.title =
      "Fablelot · " + (screen ? c.title(screen.screen_id) : c.notfound);
  }, [effective, rtl, screen, c.notfound]);
  useEffect(() => {
    if (location.path === "/") {
      go(
        kind === "storefront"
          ? "/en"
          : kind === "vendor"
            ? "/seller"
            : "/admin/policies",
      );
    }
  }, [location.path, kind, go]);
  // Signed-out visitors to Seller studio go to its sign-in page instead of an access error
  // (not on local test hosts, where browser tests open screens directly).
  useEffect(() => {
    if (kind === "vendor" && !api.authenticated && location.path !== "/seller/onboarding" && typeof location !== "undefined" && !/^localhost$|^127\.0\.0\.1$/.test(globalThis.location?.hostname ?? ""))
      go("/seller/onboarding");
  }, [location.path, kind, go, api, identityEpoch]);
  useEffect(() => {
    const id = ++sequence.current;
    const controller = new AbortController();
    setState("loading");
    setError(undefined);
    setView(undefined);
    orderPages.current.reset();
    setPageBusy(false);
    for (const cleanup of downloadCleanup.current) cleanup();
    downloadCleanup.current.clear();
    if (!screen) {
      setState("error");
      return;
    }
    const read = (id: string, path: string) => api.read(id, path, effective, location.query, controller.signal);
    // One guided checkout: the checkout page also carries the cart's hold step;
    // the cart page keeps only quantities and offers.
    const loading = kind === "storefront" && screen.screen_id === "B08"
      ? Promise.all([read("B08", location.path), read("B07", location.path.replace(/\/checkout$/, "/cart")).catch(() => undefined)])
          .then(([v, cart]) => ({ ...v, items: [...v.items, ...(cart?.items ?? []).filter(i => !v.items.some(x => x.id === i.id))], actions: mergeCheckoutActions(v.actions, cart?.actions, effective), version: v.version + (cart?.version ?? 0) }))
      : kind === "storefront" && screen.screen_id === "B07"
        ? read("B07", location.path).then(v => ({ ...v, data: { ...v.data, checkoutReady: v.actions.some(a => !cartPageActions.includes(a.id)) }, actions: v.actions.filter(a => cartPageActions.includes(a.id)) }))
        : read(screen.screen_id, location.path);
    loading
      .then((v) => {
        if (id !== sequence.current) return;
        if (
          v.actions.some(
            (action) =>
              !(screenOperations[screen.screen_id] || []).includes(action.id),
          )
        )
          throw new ApiError("denied", "UNEXPECTED_SCREEN_ACTION");
        setView(
          screen.screen_id === "B10"
            ? orderPages.current.accept(api.sessionEpoch, location.path, v)
            : v,
        );
        setState(
          v.expiresAt && Date.parse(v.expiresAt) <= Date.now()
            ? "stale"
            : v.receipt
              ? "success"
              : v.items.length ||
                  v.actions.length ||
                  v.facts?.length ||
                  v.messages?.length ||
                  v.timeline?.length ||
                  v.downloads?.length
                ? "ready"
                : "empty",
        );
      })
      .catch((e) => {
        if (id !== sequence.current || e.name === "AbortError") return;
        const err =
          e instanceof ApiError ? e : new ApiError("error", "INVALID_RESPONSE");
        if (err.state === "denied") {
          draftCache.current.clear();
          for (const cleanup of downloadCleanup.current) cleanup();
          downloadCleanup.current.clear();
          setIdentityEpoch(api.sessionEpoch);
        }
        setError(err);
        setState(err.state);
      });
    return () => controller.abort();
  }, [api, screen, location.path, location.query, effective, reload]);
  useEffect(() => {
    if (!view?.expiresAt) return;
    const timer = setInterval(() => {
      if (Date.parse(view.expiresAt!) <= Date.now())
        setState((s) => (s === "ready" || s === "success" ? "stale" : s));
    }, 1000);
    return () => clearInterval(timer);
  }, [view]);
  const title = screen ? c.title(screen.screen_id) : c.notfound;
  const guestAccountEntry=(screen?.screen_id==="A01"||(kind==="vendor"&&screen?.screen_id==="S01"))&&!api.authenticated;
  const collection = ["B01", "B02", "B04", "B05", "B10", "S03"].includes(
    screen?.screen_id || "",
  );
  const isDiscover = screen?.screen_id === "B01";
  const postPurchase = (postPurchaseScreens as readonly string[]).includes(
    screen?.screen_id || "",
  );
  const readFailure = (error: unknown) => {
    const err =
      error instanceof ApiError
        ? error
        : new ApiError("error", "INVALID_RESPONSE");
    if (err.code === "SESSION_CHANGED") return;
    orderPages.current.reset();
    stateChange(err.state, err);
  };
  async function continueOrders(next: string) {
    if (pageBusy || screen?.screen_id !== "B10" || !view || view.next !== next)
      return;
    const read = sequence.current,
      epoch = api.sessionEpoch;
    setPageBusy(true);
    try {
      const page = await loadOrderContinuation(
        api,
        orderPages.current,
        view,
        location.path,
        effective,
      );
      if (read !== sequence.current || epoch !== api.sessionEpoch) return;
      if (
        page.actions.some((action) => !screenOperations.B10.includes(action.id))
      )
        throw new ApiError("denied", "UNEXPECTED_SCREEN_ACTION");
      setView(page);
    } catch (error) {
      if (read === sequence.current && epoch === api.sessionEpoch)
        readFailure(error);
    } finally {
      if (read === sequence.current && epoch === api.sessionEpoch)
        setPageBusy(false);
    }
  }
  const renderActions = (actions: readonly Action[]) => (
    <ResourceActions
      key={
        location.path +
        ":" +
        effective +
        ":" +
        identityEpoch +
        ":" +
        view!.version
      }
      view={{ ...view!, actions: actions.filter(action => !isPublicationDeclaration(action) && !isCatalogUploadEntry(action) && !sellerDigitalField(action)) }}
      initialDrafts={draftCache.current.get(draftKey) || {}}
      onDraftsChange={(drafts) => draftCache.current.set(draftKey, drafts)}
      api={api}
      pending={pending}
      locale={effective}
      c={c}
      blocked={["loading", "stale", "denied"].includes(state)}
      onState={stateChange}
      onSellerSetupCompleted={(epoch,input,outcome)=>{
        if(accountOwnerRef.current.api!==api || screen?.screen_id!=="A01" || window.location.pathname!==location.path || window.location.search!==location.query || localeFor(window.location.pathname)!==effective || !completeSellerSetup(api,epoch,input,outcome))return false;
        sequence.current++;orderPages.current.reset();for(const cleanup of downloadCleanup.current)cleanup();downloadCleanup.current.clear();draftCache.current.clear();setView(undefined);setHistoryStatus([]);setError(undefined);setState("loading");setIdentityEpoch(api.sessionEpoch);pending.resetIdentity();return true;
      }}
      onRefresh={refresh}
    />
  );
  const nav = appScreens(kind).filter(
    (s) => s.screen_id === "O02" || (!s.route.includes("[id]") && !s.route.includes("[slug]")),
  );
  return (
    <div className={"marketplace-product is-"+kind}
      onClick={(e) => {
        const a = (e.target as HTMLElement).closest("a");
        if (
          a &&
          a.origin === window.location.origin &&
          !a.hash &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.shiftKey &&
          e.button === 0
        ) {
          e.preventDefault();
          a.closest("details")?.removeAttribute("open");
          e.currentTarget.querySelector("details.account-menu")?.removeAttribute("open");
          go(a.pathname + a.search);
        }
      }}
    >
      <a className="skip" href="#main">
        {c.pack["a11y.skipToContent"]}
      </a>
      {isPreviewHost()&&<div className="preview">{c.private}</div>}
      <header className="product-header">
        <Link
          className="brand"
          href={
            kind === "storefront"
              ? "/" + effective
              : kind === "vendor"
                ? "/seller"
                : "/admin/policies"
          }
        >
          <img
            src="/brand/wordmark.svg"
            alt="Fablelot"
            width="204"
            height="60"
          />
        </Link>
        {kind==="storefront"&&<HeaderSearch key={effective+location.query} locale={effective} query={location.query} onGo={go}/>}
        <div className="locale">
          <label htmlFor="locale">{c.pack["field.locale"]}</label>
          <select
            id="locale"
            value={effective}
            onChange={(e) => {
              const l = e.target.value as LaunchLocale;
              if (kind === "storefront")
                go(
                  location.path.replace(
                    /^\/(en|es|fr|de|ja|ar|he)(?=\/|$)/,
                    "/" + l,
                  ) + location.query,
                );
              else setWorkspaceLocale(l);
            }}
          >
            {[
              ["en", "English"],
              ["es", "Español"],
              ["fr", "Français"],
              ["de", "Deutsch"],
              ["ja", "日本語"],
            ].map(([v, n]) => (
              <option key={v} value={v}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <Session
          legacy={kind==="admin"}
          signinHref={kind==="vendor"?"/seller/onboarding":"/"+effective+"/account"}
          locale={effective}
          api={api}
          c={c}
          onChange={() => {
            for (const cleanup of downloadCleanup.current) cleanup();
            downloadCleanup.current.clear();
            sequence.current++;
            draftCache.current.clear();
            setView(undefined);
            setHistoryStatus([]);
            setIdentityEpoch(api.sessionEpoch);
            pending.resetIdentity();
            refresh();
          }}
          onError={(e) => stateChange(e.state, e)}
        />
      </header>
      <nav aria-label={c.pack["a11y.primaryNavigation"]}>
        {nav
          .filter(
            (s) =>
              kind !== "storefront" ||
              ["B01", "B05", "B07", "B10"].includes(s.screen_id),
          )
          .map((s) => (
            <Link
              key={s.screen_id}
              href={routeHref(s.route, effective, s.screen_id === "O02" ? "queue" : undefined)}
              aria-current={screen === s ? "page" : undefined}
            >
              {kind === "storefront"
                ? c.pack[
                    (
                      {
                        B01: "nav.discover",
                        B05: "nav.saved",
                        B07: "nav.cart",
                        B10: "nav.orders",
                      } as const
                    )[s.screen_id as "B01"]
                  ]
                : kind==="vendor"?sellerNavLabel(s.screen_id,effective,c.title(s.screen_id)):c.title(s.screen_id)}
            </Link>
          ))}
        {kind === "storefront" && (
          <details className="account-menu">
            <summary>{c.account}</summary>
            <div>
              {nav
                .filter(
                  (s) => !["B01", "B05", "B07", "B10"].includes(s.screen_id),
                )
                .map((s) => (
                  <Link key={s.screen_id} href={routeHref(s.route, effective, s.screen_id === "O02" ? "queue" : undefined)}>
                    {c.title(s.screen_id)}
                  </Link>
                ))}
            </div>
          </details>
        )}
      </nav>

      {kind==="storefront"&&<ProductCategoryNav locale={effective} query={location.query}/>}
      <main id="main" tabIndex={-1} data-screen={screen?.screen_id}>
        {kind==="vendor"&&screen?.screen_id==="S03"&&api.authenticated&&<AgentPanel api={api} locale={effective}/>}
        {kind==="storefront"&&screen?.screen_id==="I01"&&<DeveloperGuide/>}
        {kind==="storefront"&&screen?.screen_id==="B08"&&state!=="loading"&&!!view&&!view.actions.length&&<p className="notice">{({en:"Nothing to pay right now. ",es:"No hay nada que pagar ahora. ",fr:"Rien à payer pour le moment. ",de:"Gerade ist nichts zu bezahlen. ",ja:"現在お支払いはありません。"} as Record<string,string>)[effective]??"Nothing to pay right now. "}<Link href={"/"+effective+"/orders"}>{({en:"See your orders",es:"Ver tus pedidos",fr:"Voir vos commandes",de:"Bestellungen ansehen",ja:"注文を見る"} as Record<string,string>)[effective]??"See your orders"}</Link> · <Link href={"/"+effective+"/cart"}>{({en:"Go to your cart",es:"Ir al carrito",fr:"Aller au panier",de:"Zum Warenkorb",ja:"カートへ"} as Record<string,string>)[effective]??"Go to your cart"}</Link></p>}
        {kind==="storefront"&&screen?.screen_id==="A03"&&api.authenticated&&<PrivacyPanel api={api} locale={effective} onClosed={()=>setIdentityEpoch(api.sessionEpoch)}/>}
        {kind==="admin"&&screen?.screen_id==="O06"&&api.authenticated&&<DeletionQueuePanel api={api} locale={effective}/>}
        {kind==="admin"&&screen?.screen_id==="O02"&&api.authenticated&&<SellerReviewQueue api={api} locale={effective}/>}
        {kind==="vendor"&&screen?.screen_id==="S02"&&api.authenticated&&<SellerReviewPanel api={api} locale={effective}/>}
        {kind==="vendor"&&screen?.screen_id==="S08"&&api.authenticated&&<SellerPayoutsPanel api={api} locale={effective}/>}
        {kind==="vendor"&&screen?.screen_id==="S05"&&api.authenticated&&<SellerShippingPanel api={api} locale={effective}/>}
        {(screen?.screen_id==="A01"||(kind==="vendor"&&screen?.screen_id==="S01"))&&!api.authenticated&&<AccountEntry key={accountOwnerKey} locale={effective} sessionEpoch={accountOwnerKey} fetcher={accountFetch} onAuthenticated={(token,expected)=>{
          if(!accountAdopter.adopt(token,expected))return;
          for(const cleanup of downloadCleanup.current)cleanup();downloadCleanup.current.clear();
          sequence.current++;orderPages.current.reset();draftCache.current.clear();setView(undefined);setHistoryStatus([]);setError(undefined);setState("loading");setIdentityEpoch(api.sessionEpoch);pending.resetIdentity();refresh();
        }}/>}

        {kind==="storefront"&&screen?.screen_id==="A01"&&!api.authenticated&&<GuestPurchaseEntry key={'guest:'+accountOwnerKey} api={api} locale={effective} sessionEpoch={accountOwnerKey} fetcher={accountFetch} onAuthenticated={(token,expected)=>{
          if(!accountAdopter.adopt(token,expected))return;
          for(const cleanup of downloadCleanup.current)cleanup();downloadCleanup.current.clear();
          sequence.current++;orderPages.current.reset();draftCache.current.clear();setView(undefined);setHistoryStatus([]);setError(undefined);setState("loading");setIdentityEpoch(api.sessionEpoch);pending.resetIdentity();refresh();
        }}/>}
        {kind==="storefront"&&screen?.screen_id==="A01"&&api.authenticated&&<GuestOrderClaimEntry key={'guest-claim:'+accountOwnerKey} api={api} locale={effective} ownerKey={accountOwnerKey} onAction={(action,expectedEpoch)=>{
          if(action.id!=="identity.claim"||accountOwnerRef.current.api!==api||accountOwnerRef.current.effective!==effective||api.sessionEpoch!==expectedEpoch||window.location.pathname+window.location.search!==location.path+location.query)return;
          // A fresh ORIGINAL source View supplies the protected Action. No client-built View/version.
          refresh();
        }}/>}

        {isDiscover ? <ProductHero locale={effective} view={view} origin={api.origin}/> : !((screen?.screen_id==="A01"||(kind==="vendor"&&screen?.screen_id==="S01"))&&!api.authenticated)&&<ProductHeading screen={screen?.screen_id} locale={effective} title={title} view={view} path={location.path}/>}
        {kind === "storefront" &&
          ["B01", "B02"].includes(screen?.screen_id || "") && (
            <details className="product-search-filters" open={screen?.screen_id==="B02"}><summary>{productWords(effective).filters}</summary><Search
              key={location.query}
              query={location.query}
              locale={effective}
              c={c}
              onGo={go}
            /></details>
          )}
        <div
          className={"status " + state+(guestAccountEntry&&["empty","ready"].includes(state)?" account-entry-idle":"")}
          role={state === "error" || state === "denied" ? "alert" : "status"}
          aria-live="polite"
          aria-busy={state === "loading"}
          data-state={state}
        >
          <strong>
            {state === "loading"
              ? c.pack["a11y.busy"]
              : state === "empty"
                ? c.empty
                : state === "error"
                  ? c.error
                  : state === "denied"
                    ? c.denied
                    : state === "pending"
                      ? c.pending
                      : state === "stale"
                        ? c.stale
                        : state === "success"
                          ? c.success
                          : c.ready}
          </strong>
          {error && (
            <p>
              {publicHoldText(error.code,state,effective)}
              {error.referenceId && (
                <>
                  {" "}
                  · {c.reference}: <bdi>{error.referenceId}</bdi>
                </>
              )}
            </p>
          )}
          {["error", "stale", "pending", "denied"].includes(state) && (
            <>
              <p>{state === "pending" ? c.retryUnsafe : c.draft}</p>
              <button onClick={refresh}>{c.pack["recovery.retry"]}</button>
            </>
          )}
          {screen &&
            (["empty", "error", "denied", "pending", "stale"].includes(state) ||
              screen.screen_id === "B16") && (
              <div
                className="task-recovery"
                data-recovery-screen={screen.screen_id}
              >
                <p>{recoveryText(screen.screen_id, effective)}</p>
                {screen.screen_id === "B02" && (
                  <div className="actions">
                    {filterRecoveryLinks(location.path, location.query).map(
                      ({ name, href }) => (
                        <Link key={name} href={href}>
                          {c.pack["confirmation.cancel"]}:{" "}
                          {name === "q"
                            ? c.query
                            : name === "kind"
                              ? c.kind
                              : c.sort}
                        </Link>
                      ),
                    )}
                  </div>
                )}
                {["B04", "B09"].includes(screen.screen_id) && (
                  <Link href={"/" + effective + "/orders"}>
                    {c.pack["nav.orders"]}
                  </Link>
                )}
                {["B05", "B10"].includes(screen.screen_id) && (
                  <Link href={"/" + effective + "/account"}>{c.account}</Link>
                )}
              </div>
            )}
          {state === "empty" && kind === "storefront" && (
            <Link href={"/" + effective + "/search"}>
              {c.pack["nav.search"]}
            </Link>
          )}
        </div>
        {view && !guestAccountEntry && state !== "denied" && (
          <section
            className="resource"
            aria-label={title}
            aria-busy={state === "loading"}
          >
            {view.updatedAt&&<p className="product-updated">{c.updated} <time dateTime={view.updatedAt}>{time(view.updatedAt,effective)}</time></p>}
            {kind==="storefront"&&screen?.screen_id==="B08"&&<CheckoutSteps actions={view.actions} finished={!!view.receipt} locale={effective}/>}
            {kind==="storefront"&&screen?.screen_id==="B08"&&(()=>{const fund=view.actions.find(a=>a.id==="checkout.fund");const freezeId=(fund?.input as {freezeId?:unknown}|undefined)?.freezeId;return typeof freezeId==="string"?<WhopPayPanel key={freezeId} api={api} locale={effective} freezeId={freezeId} amount={fund?.confirmation?.amount as {currency:string;minor:string}|undefined}/>:null})()}
            {kind==="storefront"&&screen?.screen_id==="B07"&&(view.items.length>0?<p className="checkout-next"><Link className="button" href={"/"+effective+"/checkout"}>{checkoutWords(effective).toCheckout}</Link></p>:<p>{checkoutWords(effective).empty}</p>)}
            {view.reason&&<p className="notice">{publicHoldText(view.reason,state,effective)}</p>}
            {screen?.screen_id==='B09'&&view.data?.scheduledGiftStatus&&<ScheduledGiftSetupNotice status={view.data.scheduledGiftStatus} locale={effective}/>}
            {screen?.screen_id==='B06'&&<ScheduledGiftTeasers references={view.data?.scheduledGifts??[]} status={view.data?.scheduledGiftStatus} api={api} locale={effective} routeKey={location.path+':'+view.version+':'+identityEpoch}/>}
            {['B12','S11'].includes(screen?.screen_id ?? '') && <ConversationAttachmentControl actions={view.actions} api={api} pending={pending} c={c} locale={effective} disabled={['loading','stale','denied'].includes(state)} onRefresh={refresh}/>}
            {postPurchase ? (
              <PostPurchaseWorkspace
                key={location.path + ":" + effective + ":" + identityEpoch}
                screen={screen!.screen_id}
                view={view}
                state={state}
                locale={effective}
                identityEpoch={api.sessionEpoch}
                renderCard={(card) => (
                  <Product
                    key={card.id}
                    card={card}
                    apiOrigin={api.origin}
                    locale={effective}
                    c={c}
                    fallbackTitle={fallbackTitleFor(screen?.screen_id, effective)}
                  />
                )}
                renderMoney={(money) => (
                  <Price money={money} locale={effective} />
                )}
                renderTime={(instant) => time(instant, effective)}
                renderFinancialState={(value) =>
                  financialState(effective, value)
                }
                renderActions={renderActions}
                onReadFailure={readFailure}
                onPrepareCaseProposal={async (action, input) => {
                  if (!["B13", "B14"].includes(screen?.screen_id ?? "") || !view.actions.includes(action) || !action.resource || pending.blocked(action.resource.id))
                    throw new ApiError("pending", "CURRENT_CASE_PROPOSAL_REQUIRED");
                  const epoch=api.sessionEpoch,read=sequence.current;
                  const proposal=await api.prepareCaseProposal(action,input);
                  if(epoch!==api.sessionEpoch||read!==sequence.current)
                    throw new ApiError("denied", "SESSION_CHANGED");
                  return proposal;
                }}
                onDownload={async (id, kind) => {
                  const epoch = api.sessionEpoch,
                    read = sequence.current;
                  const file = await api.download(id, kind);
                  if (epoch !== api.sessionEpoch || read !== sequence.current)
                    throw new ApiError("denied", "SESSION_CHANGED");
                  downloadCleanup.current.add(saveAttachment(file));
                }}
                renderNext={(next) => (
                  <button
                    type="button"
                    disabled={pageBusy}
                    onClick={() => void continueOrders(next)}
                  >
                    {c.next}
                  </button>
                )}
              />
            ) : (
              <>
                {!isControlScreen(screen?.screen_id) && <Facts facts={view.facts} locale={effective} />}
                {!isWorkspaceScreen(screen?.screen_id) && view.data?.importPreview && (
                  <section
                    className="import-preview"
                    aria-label={c.title("S03")}
                  >
                    <h2>{c.title("S03")}</h2>
                    <ol>
                      {view.data.importPreview.rows.map((row) => (
                        <li key={row.row}>
                          <bdi>{row.row}</bdi> ·{" "}
                          {row.accepted ? c.draft : c.error}
                          {row.listingId && (
                            <bdi>
                              {" "}
                              · {row.listingId} · {c.version}{" "}
                              {row.listingVersion}
                            </bdi>
                          )}
                          {row.reasonCodes.map((code) => (
                            <p key={code}>
                              {publicHoldText(code,"error",effective)}
                            </p>
                          ))}
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
                {!isWorkspaceScreen(screen?.screen_id) && view.data?.continuingBilling && (
                  <section aria-label={c.title("S05")}>
                    <h2>{c.title("S05")}</h2>
                    {view.data.continuingBilling.map((row) => (
                      <article key={row.id}>
                        <h3>{row.label}</h3>
                        <p>{row.cadence}</p>
                        <Price money={row.amount} locale={effective} />
                        <Link href={row.cancelHref}>
                          {c.pack["confirmation.cancel"]}
                        </Link>
                      </article>
                    ))}
                  </section>
                )}
                {!isWorkspaceScreen(screen?.screen_id) && !["O04","O05"].includes(screen?.screen_id ?? "") && view.changes?.length ? (
                  <section className="notice">
                    <h2>{c.stale}</h2>
                    {view.changes.map((change, i) => (
                      <p key={i}>
                        {change.field}: <del>{change.before}</del> →{" "}
                        <ins>{change.after}</ins>
                      </p>
                    ))}
                  </section>
                ) : null}
                {["B07","S10"].includes(screen?.screen_id??"")&&<GrowthEntryPanel value={view.data?.growthEntry}/>}
                {["S01","S05"].includes(screen?.screen_id??"") ? <ProductSellerWorkspace screen={screen!.screen_id as "S01"|"S05"} view={view} state={state} locale={effective} renderCard={card=><Product card={card} apiOrigin={api.origin} locale={effective} c={c} listing={false}/>} renderMoney={money=><Price money={money} locale={effective}/>} renderTime={at=>time(at,effective)}/> : isControlScreen(screen?.screen_id) ? (
                  <ControlContent key={location.path + ":" + effective + ":" + identityEpoch + ":" + view.id + ":" + view.version}
                    screen={screen!.screen_id} view={view} state={state} locale={effective}
                    onRefresh={refresh}
                    onActionNavigate={(instance) => {
                      if (!view.actions.some(action => action.instanceId === instance && (screenOperations[screen!.screen_id] ?? []).includes(action.id))) return;
                      const form = document.getElementById(actionControlId(instance));
                      if (form instanceof HTMLFormElement) {
                        const control = form.querySelector<HTMLElement>("input:not(:disabled),textarea:not(:disabled),select:not(:disabled),button:not(:disabled)");
                        (control ?? form).focus();
                      }
                    }}
                    renderCard={(card) => <Product card={card} apiOrigin={api.origin} locale={effective} c={c} listing={false} fallbackTitle={fallbackTitleFor(screen?.screen_id, effective)} />}
                  />
                ) : isWorkspaceScreen(screen?.screen_id) ? (
                  <WorkspaceContent key={location.path + ":" + effective + ":" + identityEpoch}
                    screen={screen!.screen_id} view={view} state={state} locale={effective}
                    onRefresh={refresh}
                    renderCard={(card) => <Product card={card} apiOrigin={api.origin} locale={effective} c={c} listing={false} fallbackTitle={fallbackTitleFor(screen?.screen_id, effective)} />}
                  />
                ) : isDiscoveryScreen(screen?.screen_id) ? (
                  <DiscoveryContent key={location.path + ":" + effective + ":" + identityEpoch}
                    screen={screen!.screen_id} view={view} state={state} locale={effective}
                    path={location.path} query={location.query} apiOrigin={api.origin}
                    renderCard={(card,showMedia) => <Product card={card} apiOrigin={api.origin} locale={effective} c={c} listing={screen?.screen_id === "B03"} showMedia={showMedia} grid={productGrid(screen?.screen_id)} fallbackTitle={fallbackTitleFor(screen?.screen_id, effective)} />}
                    renderActions={renderActions}
                  />
                ) : (
                <div className={collection ? "product-grid" : "resource-cards"}>
                  {view.items.map((card) => (
                    <Product
                      key={card.id}
                      card={card}
                      apiOrigin={api.origin}
                      locale={effective}
                      c={c}
                      listing={screen?.screen_id === "B03"}
                      grid={productGrid(screen?.screen_id)}
                      fallbackTitle={fallbackTitleFor(screen?.screen_id, effective)}
                    />
                  ))}
                </div>
                )}
                {!isWorkspaceScreen(screen?.screen_id) && view.totals?.length ? (
                  <section className="totals">
                    <h2>{c.pack["confirmation.amount"]}</h2>
                    <dl>
                      {view.totals.map((row, i) => (
                        <React.Fragment key={i}>
                          <dt>{row.label}</dt>
                          <dd>
                            <Price money={row.money} locale={effective} />
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </section>
                ) : null}
                {view.messages && (
                  <ol className="messages" aria-label={c.pack["nav.messages"]}>
                    {view.messages.map((m) => (
                      <li key={m.id}>
                        <p>{m.text}</p>
                        {m.original && (
                          <details>
                            <summary>{c.original}</summary>
                            <p>{m.original}</p>
                            <p>
                              {c.translated}: {m.attribution}
                            </p>
                          </details>
                        )}
                        <time dateTime={m.sentAt}>
                          {time(m.sentAt, effective)}
                        </time>
                      </li>
                    ))}
                  </ol>
                )}
                {!isControlScreen(screen?.screen_id) && view.timeline && (
                  <ol className="timeline" aria-label={c.status}>
                    {view.timeline.map((t) => (
                      <li key={t.id}>
                        <strong>{t.label}</strong>
                        <p>{financialState(effective, t.state)}</p>
                        <time dateTime={t.at}>{time(t.at, effective)}</time>
                      </li>
                    ))}
                  </ol>
                )}
                {view.downloads?.map((d) => (
                  <div className="download" key={d.id}>
                    <p>
                      {d.label} · {c.expires} {time(d.expiresAt, effective)}
                    </p>
                    <button
                      disabled={Date.parse(d.expiresAt) <= Date.now()}
                      onClick={async () => {
                        try {
                          const file = await api.download(d.id, d.kind);
                          downloadCleanup.current.add(saveAttachment(file));
                        } catch (e) {
                          stateChange((e as ApiError).state, e as ApiError);
                        }
                      }}
                    >
                      {c.download}
                    </button>
                  </div>
                ))}

                {screen?.screen_id === "S04" && view.actions.filter(action=>!!sellerDigitalField(action)).map(action => (
                  <SellerDigitalUploadEntry key={action.instanceId + ":" + effective + ":" + identityEpoch} action={action} api={api} pending={pending} locale={effective} c={c} disabled={["loading","stale","denied"].includes(state) || (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())} onRefresh={()=>{void refresh()}}/>
                ))}
                {screen?.screen_id === "S04" && view.actions.filter(isCatalogUploadEntry).map(action => (
                  <CatalogUploadEntry key={action.instanceId + ":" + effective + ":" + identityEpoch} action={action} api={api} pending={pending} locale={effective} c={c} disabled={["loading","stale","denied"].includes(state) || (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())} onRefresh={()=>{void refresh()}}/>
                ))}
                {screen?.screen_id === "S04" && view.actions.filter(isPublicationDeclaration).map(action => (
                  <PublicationDeclaration key={action.instanceId + ":" + effective + ":" + identityEpoch} action={action} locale={effective} disabled={["loading","stale","denied"].includes(state) || (pending.blocked(action.resource!.id)||isScheduledGiftAction(action)&&pending.scheduledGiftBlocked())} onDeclare={async (source,input) => {
                    const epoch=api.sessionEpoch,read=sequence.current;
                    await api.declarePublication(source,input);
                    if(epoch!==api.sessionEpoch||read!==sequence.current)throw new ApiError("stale","VIEW_CHANGED");
                    await refresh();
                  }}/>
                ))}
                {screen?.screen_id!=="B03"&&(!["B01","B02","B04"].includes(screen?.screen_id??"")||view.actions.length>0)&&renderActions(view.actions)}
                {state === "success" && (
                  <button onClick={refresh}>{c.pack["recovery.retry"]}</button>
                )}
                {view.next && (
                  <Link className="button" href={view.next}>
                    {c.next}
                  </Link>
                )}
              </>
            )}
            <PendingRecovery
              pending={pending}
              api={api}
              c={c}
              onState={stateChange}
              onRefresh={refresh}
            />
          </section>
        )}
        {historyStatus.length > 0 && (
          <aside aria-label={c.status}>
            {historyStatus.map((s, i) => (
              <p key={i}>{s}</p>
            ))}
          </aside>
        )}

      </main>
      <footer>
        <span>Fablelot</span>
        {isPreviewHost()?<p>{c.private}</p>:<nav className="legal-links" aria-label="Legal">{LEGAL_LINKS.map(([href,label])=><a key={href} href={href}>{label}</a>)}<a href="mailto:support@fablelot.com">support@fablelot.com</a></nav>}
        {kind === "storefront" && (
          <Link href={"/" + effective + "/privacy"}>
            {c.pack["nav.privacy"]}
          </Link>
        )}
      </footer>
    </div>
  );
}
